import i18next from 'i18next';

export type DTFItemKind = 'raster' | 'svg' | 'pdf';

export interface ImportedFile {
	kind: DTFItemKind;
	name: string;
	/** Aperçu affiché à l'écran (data URL). Le fichier d'origine n'est JAMAIS dégradé. */
	src: string;
	/** Source SVG d'origine, intacte (kind = svg) */
	svgText?: string;
	/** Fichier d'origine en data URL (kind = raster | pdf) */
	originalDataUrl?: string;
	/** ratio largeur / hauteur */
	aspect: number;
}

export class ImportError extends Error {}

const err = (key: string, opts?: Record<string, unknown>) => new ImportError(i18next.t(key, opts) as string);

const readAsDataUrl = (file: File | Blob): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result));
		reader.onerror = () => reject(err('dtf.err-lecture'));
		reader.readAsDataURL(file);
	});

const readAsText = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result));
		reader.onerror = () => reject(err('dtf.err-lecture'));
		reader.readAsText(file);
	});

const loadImage = (src: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => reject(err('dtf.err-image-corrompue'));
		img.src = src;
	});

const baseName = (name: string) => name.replace(/\.[^.]+$/, '');

/** Dimensions intrinsèques d'un SVG : width/height explicites sinon viewBox. */
function svgIntrinsicSize(root: SVGSVGElement): { w: number; h: number } {
	const parseLen = (v: string | null): number | null => {
		if (!v) return null;
		const n = parseFloat(v);
		return Number.isFinite(n) && n > 0 ? n : null;
	};
	const w = parseLen(root.getAttribute('width'));
	const h = parseLen(root.getAttribute('height'));
	if (w && h) return { w, h };
	const vb = root.getAttribute('viewBox');
	if (vb) {
		const parts = vb.trim().split(/[\s,]+/).map(Number);
		if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) return { w: parts[2], h: parts[3] };
	}
	throw err('dtf.err-svg-sans-taille');
}

async function importSvg(file: File): Promise<ImportedFile> {
	const text = await readAsText(file);
	const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
	const root = doc.documentElement as unknown as SVGSVGElement;
	if (doc.querySelector('parsererror') || root.nodeName.toLowerCase() !== 'svg') {
		throw err('dtf.err-svg-invalide');
	}
	const { w, h } = svgIntrinsicSize(root);
	// viewBox garanti pour le repositionnement à l'export
	if (!root.getAttribute('viewBox')) root.setAttribute('viewBox', `0 0 ${w} ${h}`);
	const normalized = new XMLSerializer().serializeToString(root);
	const src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(normalized)))}`;
	return { kind: 'svg', name: baseName(file.name), src, svgText: normalized, aspect: w / h };
}

/**
 * Rend transparent le fond blanc d'un rendu PDF : pixels quasi blancs (ou déjà
 * transparents) CONNECTÉS AU BORD de la page. Le blanc à l'intérieur du design
 * (texte blanc, zones blanches d'un logo) est préservé — indispensable en DTF
 * où le blanc est une vraie encre.
 */
function stripWhiteBackground(ctx: CanvasRenderingContext2D, w: number, h: number): void {
	const imgData = ctx.getImageData(0, 0, w, h);
	const d = imgData.data;
	const isBg = (p: number) => {
		const i = p * 4;
		return d[i + 3] < 8 || (d[i] >= 240 && d[i + 1] >= 240 && d[i + 2] >= 240);
	};
	const visited = new Uint8Array(w * h);
	const stack: number[] = [];
	const push = (p: number) => {
		if (!visited[p] && isBg(p)) {
			visited[p] = 1;
			stack.push(p);
		}
	};
	for (let x = 0; x < w; x++) {
		push(x);
		push((h - 1) * w + x);
	}
	for (let y = 0; y < h; y++) {
		push(y * w);
		push(y * w + w - 1);
	}
	while (stack.length) {
		const p = stack.pop() as number;
		d[p * 4 + 3] = 0;
		const x = p % w;
		if (x > 0) push(p - 1);
		if (x < w - 1) push(p + 1);
		if (p >= w) push(p - w);
		if (p < w * (h - 1)) push(p + w);
	}
	ctx.putImageData(imgData, 0, 0);
}

/**
 * Rogne les marges transparentes autour du visuel : les cotes affichées dans
 * l'éditeur (et l'export) correspondent au dessin réel, pas à la page PDF
 * (souvent A4) sur laquelle le client a posé son logo.
 */
function trimTransparentMargins(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): HTMLCanvasElement {
	const w = canvas.width;
	const h = canvas.height;
	const d = ctx.getImageData(0, 0, w, h).data;
	let minX = w;
	let minY = h;
	let maxX = -1;
	let maxY = -1;
	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			if (d[(y * w + x) * 4 + 3] > 0) {
				if (x < minX) minX = x;
				if (x > maxX) maxX = x;
				if (y < minY) minY = y;
				maxY = y;
			}
		}
	}
	// page vide ou visuel bord à bord : rien à rogner
	if (maxX < 0 || (minX === 0 && minY === 0 && maxX === w - 1 && maxY === h - 1)) return canvas;
	const out = document.createElement('canvas');
	out.width = maxX - minX + 1;
	out.height = maxY - minY + 1;
	const octx = out.getContext('2d');
	if (!octx) return canvas;
	octx.drawImage(canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
	return out;
}

async function importPdf(file: File): Promise<ImportedFile> {
	const [{ getDocument, GlobalWorkerOptions }, workerUrl] = await Promise.all([
		import('pdfjs-dist'),
		import('pdfjs-dist/build/pdf.worker.min.mjs?url').then(m => m.default),
	]);
	GlobalWorkerOptions.workerSrc = workerUrl;
	const originalDataUrl = await readAsDataUrl(file);
	let pdf;
	try {
		pdf = await getDocument({ data: await file.arrayBuffer() }).promise;
	} catch (_e) {
		throw err('dtf.err-pdf-invalide');
	}
	const page = await pdf.getPage(1);
	const vp1 = page.getViewport({ scale: 1 });
	// aperçu haute résolution (~2000 px max) pour l'écran et les exports de test
	const scale = Math.min(2000 / Math.max(vp1.width, vp1.height), 8);
	const vp = page.getViewport({ scale });
	const canvas = document.createElement('canvas');
	canvas.width = Math.round(vp.width);
	canvas.height = Math.round(vp.height);
	const ctx = canvas.getContext('2d');
	if (!ctx) throw err('dtf.err-pdf-invalide');
	// background transparent : pdfjs peint sinon TOUTE la page en blanc opaque
	await page.render({ canvas, canvasContext: ctx, viewport: vp, background: 'rgba(0,0,0,0)' }).promise;
	stripWhiteBackground(ctx, canvas.width, canvas.height);
	const cropped = trimTransparentMargins(canvas, ctx);
	const src = cropped.toDataURL('image/png');
	return { kind: 'pdf', name: baseName(file.name), src, originalDataUrl, aspect: cropped.width / cropped.height };
}

async function importRaster(file: File): Promise<ImportedFile> {
	const originalDataUrl = await readAsDataUrl(file);
	const img = await loadImage(originalDataUrl);
	if (!img.naturalWidth || !img.naturalHeight) throw err('dtf.err-image-corrompue');
	return {
		kind: 'raster',
		name: baseName(file.name),
		src: originalDataUrl,
		originalDataUrl,
		aspect: img.naturalWidth / img.naturalHeight,
	};
}

const RASTER_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

/**
 * Import d'un fichier client. PNG/JPG/WEBP + SVG + PDF (+ .ai compatible PDF).
 * Le fichier d'origine est conservé intact ; seul l'aperçu est rastérisé.
 */
export async function importDTFFile(file: File, maxFichierMb: number): Promise<ImportedFile> {
	if (file.size > maxFichierMb * 1024 * 1024) {
		throw err('dtf.err-trop-lourd', { max: maxFichierMb });
	}
	const ext = (file.name.split('.').pop() || '').toLowerCase();
	if (ext === 'svg' || file.type === 'image/svg+xml') return importSvg(file);
	if (ext === 'pdf' || ext === 'ai' || file.type === 'application/pdf') return importPdf(file);
	if (RASTER_TYPES.includes(file.type) || ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) {
		return importRaster(file);
	}
	throw err('dtf.err-format', { nom: file.name });
}
